/* import __COLOCATED_TEMPLATE__ from './block-group-composer.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { type Block } from '@intercom/interblocks.ts';
import { type BlockGroup } from '../shared/suggestion-editor';
import { BlocksDocument, type ComposerPublicAPI } from '@intercom/composer-ember';
import { EntityType } from 'embercom/models/data/entity-types';
import type IntlService from 'ember-intl/services/intl';
import ArticlesComposerConfig from 'embercom/lib/articles/composer-config';
import type Model from '@ember-data/model';
import { type LocalizedKnowledgeContent } from 'embercom/objects/knowledge-hub/localized-knowledge-content';
import type ArticleContent from 'embercom/models/articles/article-content';
import { copyHeadingLinkToClipboard } from 'embercom/helpers/articles/editor-helper';
import type ClipboardService from 'embercom/services/clipboard-service';
import ENV from 'embercom/config/environment';
import { ContentSnippetComposerConfig } from 'embercom/components/content-service/composer/content-snippet-editor';
import { Status } from 'embercom/models/data/content-service/content-review-statuses';
import type Composer from '@intercom/composer-ember/lib/composer';
import type SuggestionService from 'embercom/services/suggestion-service';
import { tracked } from '@glimmer/tracking';
import { debounce } from '@ember/runloop';
import { ReviewType } from 'embercom/models/content-service/content-review-request';
import type ContentSnippet from 'embercom/models/content-service/content-snippet';
import type ContentSuggestionsService from 'embercom/services/content-suggestions-service';
import { taskFor } from 'ember-concurrency-ts';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';

interface Signature {
  Element: HTMLSpanElement;
  Args: {
    blockGroup: BlockGroup;
    contentEntityType: EntityType;
    activeContent: LocalizedKnowledgeContent & Model;
    setFocusedComposer: (composer?: Composer) => void;
    blockGroupIndex: number;
    onComposerContentUpdated: () => void;
  };
}

export default class BlockGroupComposer extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intercomEventService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare clipboardService: ClipboardService;
  @service declare suggestionService: SuggestionService;
  @service declare contentSuggestionsService: ContentSuggestionsService;
  @service declare permissionsService: $TSFixMe;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;

  @tracked insertionOrDeletionMarkAdded = false;

  get composerConfig() {
    if (this.args.contentEntityType === EntityType.ContentSnippet) {
      let config = new ContentSnippetComposerConfig({
        eventService: this.intercomEventService,
        intl: this.intl,
        placeholder:
          this.args.blockGroup.reviewRequest?.get('reviewType') === ReviewType.CREATE_CONTENT
            ? this.intl.t('ai-content-library.content-snippet.editor.placeholder')
            : '',
      });
      config.allowedInline.push('insertion', 'deletion');
      config.enableHighlighting = true;
      return config;
    }
    return this.articleComposerConfig;
  }

  get articleContent() {
    return this.args.activeContent as unknown as ArticleContent;
  }

  get contentSnippet() {
    return this.args.activeContent as ContentSnippet;
  }

  get articleComposerConfig() {
    let eventService = this.intercomEventService;
    let analytics = {
      trackAnalyticsEvent: (eventName: any, attrs: any) => {
        eventService.trackAnalyticsEvent({
          action: eventName,
          object: 'article',
          place: 'composer',
          ...attrs,
        });
      },
    };

    let anchorLinkOnClick = (heading: any) => {
      copyHeadingLinkToClipboard(
        heading,
        this.articleContent,
        this.notificationsService,
        this.intl,
        this.clipboardService,
        this.intercomEventService,
        this.articleContent.locale ?? '',
      );
    };

    let config = new ArticlesComposerConfig(
      this.appService.app,
      this.articleContent.textDirection,
      analytics,
      anchorLinkOnClick,
      true,
      false,
    );

    if (this.args.blockGroup.reviewRequest?.get('reviewType') !== ReviewType.CREATE_CONTENT) {
      config.placeholder = '';
    }

    config.allowedInline.push('insertion', 'deletion');
    config.enableHighlighting = true;

    return config;
  }

  get blocks() {
    if (this.args.blockGroup.reviewRequest?.get('status') === Status.Rejected) {
      return this.args.blockGroup.blocksBefore ?? [];
    }
    return this.args.blockGroup.blocks ?? [];
  }

  get blocksDocument() {
    return new BlocksDocument(this.blocks);
  }

  @action
  composerChanged(newBlocksDocument: BlocksDocument) {
    // Using debounce to avoid updating the blocks array too often since we're splicing the blocks array
    debounce(
      this,
      this.updateBlocks,
      newBlocksDocument.blocks,
      this.args.blockGroup,
      ENV.APP._200MS,
    );
    debounce(this, this.args.onComposerContentUpdated, ENV.APP._200MS);
    // if content review request is create content, we autosave based on changes to the edited blocks
    if (this.args.blockGroup.reviewRequest?.get('reviewType') === ReviewType.CREATE_CONTENT) {
      if (!this.suggestionService.hasDirtyContent && this.insertionOrDeletionMarkAdded) {
        this.suggestionService.hasDirtyContent = true;
      }
      if (!this.insertionOrDeletionMarkAdded) {
        this.insertionOrDeletionMarkAdded = true;
      }
      taskFor(this.suggestionService.autoSaveContentTask).perform();
      // if content review request is not create content, we do not want to set hasDirtyContent to true if we edit the edited blocks
      // this is because we do not persist the changes to them unless we accept the review request
      // if the review request has been actioned we should allow the user to save changes made to those blocks
    } else if (
      (this.args.blockGroup.isEdited &&
        this.args.blockGroup.reviewRequest?.get('status') !== Status.Pending) ||
      (!this.args.blockGroup.isEdited && !this.suggestionService.hasDirtyContent)
    ) {
      this.suggestionService.hasDirtyContent = true;
    }
  }

  @action
  handleComposerReady(composerApi: ComposerPublicAPI) {
    this.args.blockGroup.composerApi = composerApi;
    this.setFocusedComposer();
    this.highlightBlocksIfNeeded(composerApi);
  }

  private highlightBlocksIfNeeded(composerApi: ComposerPublicAPI) {
    if (this.args.blockGroup.reviewRequest?.isUnderperformingContent) {
      return;
    }
    if (this.args.blockGroup.reviewRequest?.get('status') === Status.Pending) {
      composerApi.composer.commands.highlightAll();
    }
  }

  @action
  handleComposerFocus() {
    this.setFocusedComposer();
  }

  @action
  setFocusedComposer() {
    if (this.args.contentEntityType === EntityType.ArticleContent) {
      this.args.setFocusedComposer(this.args.blockGroup.composerApi?.composer);
    }
  }

  private updateBlocks(value: Block[], blockGroup: BlockGroup) {
    // Using splice to avoid creating a new reference to the blocks array
    // If we do blockGroup.blocksBefore = [...value], this will create a new reference to the blocks array and we don't want that
    // This is important in scenarios where we need to update the indices of where a suggestion was made
    // This will ensure that the reference to the blocks array is the same at all times
    if (blockGroup.reviewRequest?.get('status') === Status.Rejected) {
      blockGroup.blocksBefore?.splice(0, blockGroup.blocksBefore.length, ...value);
    } else {
      blockGroup.blocks.splice(0, blockGroup.blocks.length, ...value);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ContentReview::BlockGroupComposer': typeof BlockGroupComposer;
    'knowledge-hub/content-editor/content-review/block-group-composer': typeof BlockGroupComposer;
  }
}
